import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useCreateProjectMutation } from "@/hooks/useBackendData";
import { useAppStore } from "@/state/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
}

const COLOR_OPTIONS = ["#6C3CF5", "#0DC5D6", "#E0A100", "#1E8E3E", "#D93025"];
const ICON_OPTIONS = ["folder", "rocket_launch", "analytics", "build", "campaign"];

export const ProjectSetupSheet = ({ open, onClose }: Props): JSX.Element | null => {
  const goals = useAppStore((state) => state.goals);
  const members = useAppStore((state) => state.members);
  const agents = useAppStore((state) => state.agents);
  const setActiveProject = useAppStore((state) => state.setActiveProject);
  const createProjectMutation = useCreateProjectMutation();
  const connectedAgents = useMemo(() => agents.filter((agent) => agent.status === "active"), [agents]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [primaryGoalId, setPrimaryGoalId] = useState<string>("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [defaultAgentId, setDefaultAgentId] = useState<string>("");

  const canSubmit = useMemo(() => title.trim().length >= 2, [title]);

  if (!open) return null;

  return (
    <Sheet open={open} title="Project Setup" onClose={onClose} height="80">
      <div className="space-y-4">
        <InputField
          label="Title *"
          inputProps={{
            value: title,
            onChange: (event) => setTitle(event.target.value),
            placeholder: "Growth Sprint"
          }}
        />

        <InputField
          kind="textarea"
          label="Description / Context"
          textareaProps={{
            value: description,
            onChange: (event) => setDescription(event.target.value),
            placeholder: "What this project aims to achieve and constraints for AI planning."
          }}
        />

        <div>
          <p className="mb-2 text-sm font-medium">Color</p>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="h-8 w-8 rounded-full border"
                style={{
                  background: option,
                  borderColor: color === option ? "var(--accent-primary)" : "var(--border-default)",
                  boxShadow: color === option ? "0 0 0 2px color-mix(in srgb, var(--accent-primary) 30%, transparent)" : "none"
                }}
                onClick={() => setColor(option)}
                aria-label={`Select ${option}`}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Icon</p>
          <div className="flex flex-wrap gap-2">
            {ICON_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: icon === option ? "var(--accent-primary)" : "var(--border-default)",
                  background: icon === option ? "var(--accent-primary-subtle)" : "var(--bg-tertiary)"
                }}
                onClick={() => setIcon(option)}
              >
                <span className="material-symbols-outlined text-base align-middle">{option}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Linked Goals</p>
          {goals.length === 0 ? <p className="text-xs text-[var(--text-tertiary)]">No goals yet.</p> : null}
          <div className="space-y-2">
            {goals.map((goal) => {
              const checked = selectedGoalIds.includes(goal.id);
              return (
                <label key={goal.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setSelectedGoalIds((prev) => {
                        if (event.target.checked) return [...prev, goal.id];
                        return prev.filter((id) => id !== goal.id);
                      });
                      if (!event.target.checked && primaryGoalId === goal.id) {
                        setPrimaryGoalId("");
                      }
                    }}
                  />
                  <span>{goal.title}</span>
                </label>
              );
            })}
          </div>
        </div>

        <label className="block text-sm text-[var(--text-secondary)]">
          Primary Goal
          <select
            className="input mt-1"
            value={primaryGoalId}
            onChange={(event) => setPrimaryGoalId(event.target.value)}
            disabled={selectedGoalIds.length === 0}
          >
            <option value="">None</option>
            {goals
              .filter((goal) => selectedGoalIds.includes(goal.id))
              .map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
          </select>
        </label>

        <div>
          <p className="mb-2 text-sm font-medium">Assigned Members</p>
          {members.length === 0 ? <p className="text-xs text-[var(--text-tertiary)]">No members yet.</p> : null}
          <div className="space-y-2">
            {members.map((member) => {
              const checked = selectedMemberIds.includes(member.userId);
              return (
                <label key={member.userId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setSelectedMemberIds((prev) => {
                        if (event.target.checked) return [...prev, member.userId];
                        return prev.filter((id) => id !== member.userId);
                      });
                    }}
                  />
                  <span>
                    {member.name} ({member.role})
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <label className="block text-sm text-[var(--text-secondary)]">
          Default Agent
          <select className="input mt-1" value={defaultAgentId} onChange={(event) => setDefaultAgentId(event.target.value)}>
            <option value="">None</option>
            {connectedAgents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </label>

        <Button
          className="w-full"
          disabled={!canSubmit || createProjectMutation.isPending}
          onClick={async () => {
            try {
              const project = await createProjectMutation.mutateAsync({
                title: title.trim(),
                description: description.trim() || undefined,
                color,
                icon,
                goalIds: selectedGoalIds,
                primaryGoalId: primaryGoalId || undefined,
                memberIds: selectedMemberIds,
                defaultAgentId: defaultAgentId || undefined
              });
              setActiveProject(project.id);
              setTitle("");
              setDescription("");
              setColor(COLOR_OPTIONS[0]);
              setIcon(ICON_OPTIONS[0]);
              setSelectedGoalIds([]);
              setPrimaryGoalId("");
              setSelectedMemberIds([]);
              setDefaultAgentId("");
              onClose();
            } catch {
              // toast emitted by mutation
            }
          }}
        >
          {createProjectMutation.isPending ? "Creating..." : "Create Project"}
        </Button>
      </div>
    </Sheet>
  );
};
